import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import {
  getContextIssueLinks,
  GetContextIssueLinksData as _GetContextIssueLinksData,
  GetContextIssueLinksResponse as _GetContextIssueLinksResponse,
  createIssueLink,
  CreateIssueLinkData,
  CreateIssueLinkResponse as _CreateIssueLinkResponse,
  deleteContextIssueLink,
  DeleteContextIssueLinkData,
  IssueLinksRequest as _IssueLinksRequest,
  IssueLinksResponse,
} from '@data-access/connect/tc-issue-management';
import {
  getFileDetails,
  GetFileDetailsResponse,
} from '@data-access/connect/tc-tcps';

export interface IReference {
  id?: string;
  url?: string;
  resourceId?: string;
  description: string;
  type?: 'FILE' | 'URL' | 'OBJECT' | 'CLASH';
  thumbnailUrl?: string;
  name?: string;
  versionId?: string;
  properties?: Record<string, string>;
}

export interface CreateReferenceData {
  contextId: string;
  issueId: string;
  reference: IReference;
}

export interface DeleteReferenceData {
  contextId: string;
  issueId: string;
  referenceId: string;
}

export class IssueReferenceApiService {
  constructor(
    private readonly issueManagementClient: Client,
    private readonly queryClient: QueryClient,
    private readonly tcpsClient?: Client
  ) {}

  // Query for fetching issue references
  public getIssueReferencesQuery(
    contextId: Accessor<string | undefined>,
    issueId: Accessor<string | undefined>
  ): UseQueryResult<IReference[], Error> {
    return useQuery(() => ({
      queryKey: ['issue-references', contextId(), issueId()],
      queryFn: async (): Promise<IReference[]> => {
        const context = contextId();
        const issue = issueId();
        if (!context || !issue) {
          throw new Error('Context ID and Issue ID are required');
        }

        const response = await getContextIssueLinks({
          client: this.issueManagementClient,
          path: { contextId: context, issueId: issue },
        });

        if (response.error) {
          throw new Error('Failed to get issue references');
        }

        // Convert IssueLinksResponse[] to IReference[]
        return (response.data?.items || []).map((link: unknown) => {
          const typedLink = link as IssueLinksResponse & {
            properties?: Record<string, string>;
          };
          const referenceType = this.mapResourceTypeToReferenceType(
            typedLink.resourceType,
            typedLink.resourceId
          );
          return {
            id: typedLink.id,
            description: typedLink.description || 'Untitled reference',
            type: referenceType,
            resourceId:
              referenceType === 'URL' ? undefined : typedLink.resourceId,
            url: referenceType === 'URL' ? typedLink.resourceId : undefined,
            properties: typedLink.properties,
          };
        });
      },
      enabled: !!contextId() && !!issueId(),
    }));
  }

  // Imperative method for fetching issue references on-demand (e.g., in event handlers)
  public async fetchIssueReferences(
    contextId: string,
    issueId: string
  ): Promise<IReference[]> {
    const response = await getContextIssueLinks({
      client: this.issueManagementClient,
      path: { contextId, issueId },
    });

    if (response.error) {
      throw new Error('Failed to get issue references');
    }

    // Convert IssueLinksResponse[] to IReference[]
    return (response.data?.items || []).map((link: unknown) => {
      const typedLink = link as IssueLinksResponse & {
        properties?: Record<string, string>;
      };
      const referenceType = this.mapResourceTypeToReferenceType(
        typedLink.resourceType,
        typedLink.resourceId
      );
      return {
        id: typedLink.id,
        description: typedLink.description || 'Untitled reference',
        type: referenceType,
        resourceId: referenceType === 'URL' ? undefined : typedLink.resourceId,
        url: referenceType === 'URL' ? typedLink.resourceId : undefined,
        properties: typedLink.properties,
      };
    });
  }

  // Helper method to map API resource types to our reference types
  private mapResourceTypeToReferenceType(
    resourceType: string,
    resourceId?: string
  ): IReference['type'] {
    switch (resourceType.toUpperCase()) {
      case 'FILE':
        // If the resourceId looks like a URL (starts with http), treat it as URL type
        if (
          resourceId &&
          (resourceId.startsWith('http://') ||
            resourceId.startsWith('https://'))
        ) {
          return 'URL';
        }
        return 'FILE';
      case 'CLASH_ITEM':
        return 'CLASH';
      case 'VIEW_3D':
      case 'VIEW_2D':
        return 'OBJECT';
      case 'ISSUE':
        return 'OBJECT';
      default:
        return 'OBJECT';
    }
  }

  // Helper method to map reference types to API resource types
  private mapReferenceTypeToResourceType(
    referenceType: IReference['type']
  ): 'FILE' | 'CLASH_ITEM' | 'VIEW_3D' | 'VIEW_2D' | 'ISSUE' {
    switch (referenceType) {
      case 'FILE':
        return 'FILE';
      case 'URL':
        return 'FILE'; // URL references are stored as FILE type with resourceId as URL
      case 'CLASH':
        return 'CLASH_ITEM';
      case 'OBJECT':
        return 'VIEW_3D';
      default:
        return 'FILE';
    }
  }

  // Query for getting file details to populate thumbnail information
  public getFileDetailsQuery(
    fileId: Accessor<string | undefined>
  ): UseQueryResult<GetFileDetailsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['file-details', fileId()],
      queryFn: async (): Promise<GetFileDetailsResponse> => {
        const id = fileId();
        if (!id || !this.tcpsClient) {
          throw new Error('File ID and TCPS client are required');
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = await getFileDetails({
          client: this.tcpsClient as any,
          path: { fileId: id },
        });

        if (response.error) {
          throw new Error('Failed to get file details');
        }

        return response.data;
      },
      enabled: !!fileId() && !!this.tcpsClient,
    }));
  }

  // Mutation for adding a reference to an issue
  public addReferenceMutation(): UseMutationResult<
    IReference,
    Error,
    CreateReferenceData
  > {
    return useMutation(() => ({
      mutationFn: async (data: CreateReferenceData): Promise<IReference> => {
        // Create the issue link using the API
        const createLinkData: CreateIssueLinkData = {
          path: {
            contextId: data.contextId,
            issueId: data.issueId,
          },
          body: {
            resourceType: this.mapReferenceTypeToResourceType(
              data.reference.type || 'FILE'
            ),
            resourceId: data.reference.resourceId || data.reference.url || '',
            description: data.reference.description,
            ...(data.reference.properties && {
              properties: data.reference.properties,
            }),
          },
          url: '/context/{contextId}/issues/{issueId}/links',
        };

        const response = await createIssueLink({
          client: this.issueManagementClient,
          ...createLinkData,
        });

        if (response.error) {
          throw new Error('Failed to create issue reference');
        }

        const apiResponse = response.data! as IssueLinksResponse & {
          properties?: Record<string, string>;
        };
        const referenceType = this.mapResourceTypeToReferenceType(
          apiResponse.resourceType,
          apiResponse.resourceId
        );
        const newReference: IReference = {
          id: apiResponse.id,
          description: apiResponse.description || data.reference.description,
          type: referenceType,
          resourceId:
            referenceType === 'URL' ? undefined : apiResponse.resourceId,
          url: referenceType === 'URL' ? apiResponse.resourceId : undefined,
          properties: apiResponse.properties || data.reference.properties,
        };

        // If it's a file reference, fetch thumbnail information
        if (
          newReference.type === 'FILE' &&
          newReference.resourceId &&
          this.tcpsClient
        ) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const fileDetailsResponse = await getFileDetails({
              client: this.tcpsClient as any,
              path: { fileId: newReference.resourceId },
            });

            if (!fileDetailsResponse.error) {
              const fileDetails = fileDetailsResponse.data;
              newReference.thumbnailUrl = fileDetails.thumbnailUrl?.[0];
              newReference.name = fileDetails.name;
              newReference.versionId = fileDetails.versionId;
            }
          } catch (error) {
            console.warn('Failed to fetch file details for reference:', error);
          }
        }

        return newReference;
      },
      onSuccess: (_, variables) => {
        // Invalidate references queries
        void this.queryClient.invalidateQueries({
          queryKey: [
            'issue-references',
            variables.contextId,
            variables.issueId,
          ],
        });
      },
    }));
  }

  // Mutation for removing a reference from an issue
  public removeReferenceMutation(): UseMutationResult<
    void,
    Error,
    DeleteReferenceData
  > {
    return useMutation(() => ({
      mutationFn: async (data: DeleteReferenceData): Promise<void> => {
        // Delete the issue link using the API
        const deleteLinkData: DeleteContextIssueLinkData = {
          path: {
            contextId: data.contextId,
            issueId: data.issueId,
            linkId: data.referenceId,
          },
          url: '/context/{contextId}/issues/{issueId}/links/{linkId}',
        };

        const response = await deleteContextIssueLink({
          client: this.issueManagementClient,
          ...deleteLinkData,
        });

        if (response.error) {
          throw new Error('Failed to remove issue reference');
        }
      },
      onSuccess: (_, variables) => {
        // Invalidate references queries
        void this.queryClient.invalidateQueries({
          queryKey: [
            'issue-references',
            variables.contextId,
            variables.issueId,
          ],
        });
      },
    }));
  }
}
